import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Star, ArrowLeft, X, BellRing, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AppIcon } from "../components/AppCard";
import { Logo } from "../components/Logo";
import { BooksPanel } from "../components/BooksPanel";

const COLORS = ["#FFC72C", "#FF5A36", "#38BDF8", "#34D399", "#A78BFA", "#FDA4AF"];

const emptyForm = {
  name: "",
  description: "",
  age_range: "Ages 6-12",
  category: "",
  icon: "🎮",
  color: "#FFC72C",
  tags: "",
  screenshots: "",
  play_url: "",
  app_store_url: "",
  google_play_url: "",
  featured: false,
};

export default function Admin() {
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [announcing, setAnnouncing] = useState(null);

  const load = useCallback(() => {
    api.get("/apps").then((r) => setApps(r.data)).catch(() => setApps([]));
    api.get("/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => load())
      .catch(() => navigate("/admin/login"));
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("kitoe_token");
    navigate("/admin/login");
  };

  const startEdit = (app) =>
    setForm({
      ...app,
      tags: (app.tags || []).join(", "),
      screenshots: (app.screenshots || []).join(", "),
      play_url: app.play_url || "",
    });

  const splitList = (value) =>
    value.split(",").map((t) => t.trim()).filter(Boolean);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      age_range: form.age_range,
      category: form.category,
      icon: form.icon,
      color: form.color,
      tags: splitList(form.tags),
      screenshots: splitList(form.screenshots),
      play_url: form.play_url || "",
      app_store_url: form.app_store_url || "#",
      google_play_url: form.google_play_url || "#",
      featured: form.featured,
    };
    try {
      if (form.id) {
        const { data } = await api.put(`/apps/${form.id}`, payload);
        if (typeof data.announced === "number") {
          toast.success(`Game updated — launch announcement sent to ${data.announced} subscriber(s)!`);
        } else {
          toast.success("Game updated");
        }
      } else {
        const { data } = await api.post("/apps", payload);
        if (typeof data.announced === "number") {
          toast.success(`Game added — launch announcement sent to ${data.announced} subscriber(s)!`);
        } else {
          toast.success("Game added — it's live on the site!");
        }
      }
      setForm(null);
      load();
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (app) => {
    if (!window.confirm(`Delete "${app.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/apps/${app.id}`);
      toast.success("Game deleted");
      load();
    } catch {
      toast.error("Could not delete. Please try again.");
    }
  };

  const announce = async (app) => {
    if (
      !window.confirm(
        `Email a launch announcement for "${app.name}" to ${subscribers.length} subscriber(s)?`
      )
    )
      return;
    setAnnouncing(app.id);
    try {
      const { data } = await api.post(`/apps/${app.id}/announce`);
      toast.success(`Announcement sent to ${data.sent} subscriber(s)`);
    } catch {
      toast.error("Could not send the announcement. Please try again.");
    } finally {
      setAnnouncing(null);
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
        {label}
      </label>
      <input
        data-testid={`admin-form-${key}-input`}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
        {...props}
      />
    </div>
  );

  return (
    <main className="min-h-screen px-4 sm:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" data-testid="admin-logo-link">
            <Logo size={40} />
          </Link>
          <div className="flex gap-2.5">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border-2 border-ink font-bold text-sm hover:-translate-y-0.5 hover:shadow-brutal-sm transition-all"
            >
              <ArrowLeft size={14} /> View site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-ink text-cream border-2 border-ink font-bold text-sm hover:bg-coral transition-colors"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
              Studio Admin
            </span>
            <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tight">
              Your games
            </h1>
          </div>
          <button
            onClick={() => setForm({ ...emptyForm })}
            data-testid="admin-add-app-button"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-mint text-ink font-display font-extrabold border-2 border-ink shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
          >
            <Plus size={18} /> Add a game
          </button>
        </div>

        {/* GAME LIST */}
        <div data-testid="admin-apps-list" className="mt-8 grid md:grid-cols-2 gap-5">
          {apps === null ? (
            <p className="text-ink/50 font-bold">Loading…</p>
          ) : apps.length === 0 ? (
            <div className="md:col-span-2 rounded-3xl border-2 border-dashed border-ink/30 bg-white/60 p-12 text-center">
              <p className="font-display font-bold text-xl">No games yet</p>
              <p className="mt-1 text-ink/60">Click "Add a game" to publish your first app.</p>
            </div>
          ) : (
            apps.map((app) => (
              <div
                key={app.id}
                data-testid={`admin-app-row-${app.id}`}
                className="rounded-3xl border-2 border-ink bg-white shadow-brutal-sm p-5 flex items-start gap-4"
              >
                <AppIcon icon={app.icon} color={app.color} box="w-14 h-14" text="text-3xl" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-bold text-lg tracking-tight">{app.name}</h3>
                    {app.featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-sun border border-ink">
                        <Star size={10} fill="currentColor" /> Featured
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ink/60 line-clamp-2 mt-0.5">{app.description}</p>
                  <p className="text-xs font-bold text-ink/50 mt-1">
                    {app.age_range}
                    {app.screenshots?.length > 0 && ` · ${app.screenshots.length} screenshot${app.screenshots.length > 1 ? "s" : ""}`}
                    {app.play_url && " · playable online"}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => startEdit(app)}
                    data-testid={`admin-edit-app-button-${app.id}`}
                    className="p-2.5 rounded-xl bg-skykit/20 border-2 border-ink hover:bg-skykit/40 transition-colors"
                    aria-label={`Edit ${app.name}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => announce(app)}
                    data-testid={`admin-announce-app-button-${app.id}`}
                    disabled={announcing === app.id}
                    className="p-2.5 rounded-xl bg-sun/40 border-2 border-ink hover:bg-sun transition-colors disabled:opacity-50"
                    aria-label={`Send launch announcement for ${app.name}`}
                    title="Email launch announcement to subscribers"
                  >
                    <Megaphone size={16} />
                  </button>
                  <button
                    onClick={() => remove(app)}
                    data-testid={`admin-delete-app-button-${app.id}`}
                    className="p-2.5 rounded-xl bg-coral/15 border-2 border-ink hover:bg-coral/30 transition-colors"
                    aria-label={`Delete ${app.name}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* LAUNCH LIST */}
        <section className="mt-14" data-testid="admin-subscribers-section">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl border-2 border-ink bg-sun flex items-center justify-center">
              <BellRing size={18} />
            </span>
            <h2 className="font-display font-black text-2xl tracking-tight">
              Launch list{" "}
              <span className="text-ink/40 text-xl">({subscribers.length})</span>
            </h2>
          </div>
          <p className="mt-2 text-sm text-ink/60">
            Players who asked to be notified when games launch. Announcements go out
            automatically when you add a real App Store or Google Play link to a game —
            or use the megaphone button on any game to announce it manually.
          </p>
          <div className="mt-5 rounded-3xl border-2 border-ink bg-white shadow-brutal-sm p-5">
            {subscribers.length === 0 ? (
              <p className="text-ink/50 font-bold text-sm">No subscribers yet — share the site to grow the list!</p>
            ) : (
              <ul className="divide-y divide-ink/10">
                {subscribers.map((s) => (
                  <li
                    key={s.id}
                    data-testid={`subscriber-row-${s.id}`}
                    className="py-2.5 flex items-center justify-between gap-3 text-sm font-medium"
                  >
                    <span>{s.email}</span>
                    <span className="text-xs text-ink/40">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <BooksPanel />

        {/* FORM MODAL */}
        {form && (
          <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <div
              data-testid="admin-app-form"
              className="w-full max-w-2xl rounded-[2rem] border-2 border-ink bg-white shadow-brutal-lg p-6 sm:p-8 my-8"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-display font-black text-2xl tracking-tight">
                  {form.id ? "Edit game" : "Add a new game"}
                </h2>
                <button
                  onClick={() => setForm(null)}
                  data-testid="admin-form-close-button"
                  className="p-2 rounded-full border-2 border-ink hover:bg-ink/5"
                  aria-label="Close form"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={save} className="mt-6 grid sm:grid-cols-2 gap-4">
                {field("name", "App name", { required: true, placeholder: "QuizKids Adventure" })}
                {field("age_range", "Age range", { required: true, placeholder: "Ages 6-12" })}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
                    Description
                  </label>
                  <textarea
                    data-testid="admin-form-description-input"
                    required
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl border-2 border-ink bg-cream font-medium focus:outline-none focus:ring-2 focus:ring-coral"
                    placeholder="A fun educational quiz app for kids…"
                  />
                </div>
                {field("category", "Category", { placeholder: "Quiz & Trivia" })}
                {field("icon", "Icon (emoji or image URL)", { placeholder: "🚀" })}
                <div className="sm:col-span-2">
                  {field("screenshots", "Screenshot image URLs (comma separated)", {
                    placeholder: "/screenshots/shot1.png, https://…/shot2.png",
                  })}
                </div>
                {field("play_url", "Play online link (optional)", {
                  placeholder: "https://your-game.emergent.host",
                })}
                {field("tags", "Tags (comma separated)", { placeholder: "Animals, Space, Science" })}
                <div>
                  <label className="block text-xs font-extrabold uppercase tracking-widest text-ink/60 mb-1.5">
                    Icon color
                  </label>
                  <div className="flex gap-2 flex-wrap pt-1">
                    {COLORS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        data-testid={`admin-form-color-${c}`}
                        onClick={() => setForm({ ...form, color: c })}
                        className={`w-9 h-9 rounded-xl border-2 border-ink transition-transform ${
                          form.color === c ? "scale-110 ring-2 ring-ink ring-offset-2" : ""
                        }`}
                        style={{ backgroundColor: c }}
                        aria-label={`Color ${c}`}
                      />
                    ))}
                  </div>
                </div>
                {field("app_store_url", "App Store link", { placeholder: "https://apps.apple.com/…" })}
                {field("google_play_url", "Google Play link", { placeholder: "https://play.google.com/…" })}
                <p className="sm:col-span-2 -mt-1 text-xs text-ink/50">
                  Tip: the first time you save a real App Store or Google Play link for a
                  game, a launch announcement is emailed to your Launch list automatically.
                </p>

                <label className="sm:col-span-2 inline-flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="admin-form-featured-checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="w-5 h-5 accent-[#FF5A36]"
                  />
                  <span className="font-bold text-sm">Feature this game on the home page</span>
                </label>

                <div className="sm:col-span-2 flex gap-3 pt-2">
                  <button
                    type="submit"
                    data-testid="admin-form-submit-button"
                    disabled={saving}
                    className="flex-1 px-6 py-4 rounded-2xl bg-ink text-cream font-display font-extrabold text-lg border-2 border-ink shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal hover:bg-coral transition-all duration-200 disabled:opacity-60"
                  >
                    {saving ? "Saving…" : form.id ? "Save changes" : "Publish game"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(null)}
                    data-testid="admin-form-cancel-button"
                    className="px-6 py-4 rounded-2xl bg-white font-display font-extrabold text-lg border-2 border-ink hover:bg-ink/5 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
